import {
  addDecimals,
  compareDecimalStrings,
  isDecimalString,
  subtractDecimals,
} from "@/shared/utils/decimal";

import {
  LedgerDirection,
  type LedgerAccountRollupRow,
  type LedgerCurrencyTotal,
  type LedgerEntryGroup,
  type LedgerLeg,
  type LedgerOperationView,
} from "../types/paymentsLedger.types";

/**
 * Chronological comparator over `seq`. It is a bigint serialized as a string, so neither `Number` (loses
 * precision past 2^53) nor a plain string compare ("10" < "9") is correct — `BigInt` is. A malformed value
 * degrades to a string compare instead of throwing, so one bad row never blanks the page.
 */
export function compareLedgerSeq(a: string, b: string): number {
  try {
    const left = BigInt(a);
    const right = BigInt(b);
    if (left < right) return -1;
    if (left > right) return 1;
    return 0;
  } catch {
    return a.localeCompare(b);
  }
}

/**
 * Merge the leg lists of an operation's own `operation_type` and of `REVERSE` into one chronological trail.
 * Duplicate leg ids are dropped — the two queries are disjoint by construction, but a future backend change
 * that returned reversals under both types must not double-count money.
 */
export function mergeLedgerLegs(...lists: LedgerLeg[][]): LedgerLeg[] {
  const byId = new Map<string, LedgerLeg>();
  for (const list of lists) {
    for (const leg of list) byId.set(leg.id, leg);
  }
  return [...byId.values()].sort((a, b) => compareLedgerSeq(a.seq, b.seq));
}

/** Accumulator that keeps debit/credit sums per currency without ever touching `Number`. */
class CurrencyTotals {
  private readonly debit = new Map<string, string>();
  private readonly credit = new Map<string, string>();
  private readonly order: string[] = [];
  /** Turns false when a money string is not a valid decimal — such a total can never be declared balanced. */
  private trustworthy = true;

  add(leg: LedgerLeg): void {
    if (!this.debit.has(leg.currency)) {
      this.order.push(leg.currency);
      this.debit.set(leg.currency, "0");
      this.credit.set(leg.currency, "0");
    }
    if (!isDecimalString(leg.amount)) {
      this.trustworthy = false;
      return;
    }
    const bucket = leg.direction === LedgerDirection.DEBIT ? this.debit : this.credit;
    bucket.set(leg.currency, addDecimals(bucket.get(leg.currency) ?? "0", leg.amount));
  }

  build(): LedgerCurrencyTotal[] {
    return this.order.map((currency) => {
      const debit = this.debit.get(currency) ?? "0";
      const credit = this.credit.get(currency) ?? "0";
      return {
        currency,
        debit,
        credit,
        isBalanced: this.trustworthy && compareDecimalStrings(debit, credit) === 0,
      };
    });
  }
}

function buildRollup(legs: LedgerLeg[]): LedgerAccountRollupRow[] {
  const rows = new Map<string, LedgerAccountRollupRow>();
  for (const leg of legs) {
    const key = `${leg.account_id}|${leg.currency}`;
    const existing = rows.get(key);
    const row: LedgerAccountRollupRow = existing ?? {
      key,
      account: leg.account,
      currency: leg.currency,
      debit: "0",
      credit: "0",
      net: "0",
      netSide: null,
      lastBalanceAfter: leg.balance_after,
      legCount: 0,
    };
    if (isDecimalString(leg.amount)) {
      if (leg.direction === LedgerDirection.DEBIT) row.debit = addDecimals(row.debit, leg.amount);
      else row.credit = addDecimals(row.credit, leg.amount);
    }
    row.legCount += 1;
    // Legs arrive ordered by `seq`, so the last one seen for an account is its latest state.
    row.lastBalanceAfter = leg.balance_after;
    rows.set(key, row);
  }

  for (const row of rows.values()) {
    row.net = subtractDecimals(row.debit, row.credit);
    const sign = compareDecimalStrings(row.net, "0");
    row.netSide = sign > 0 ? LedgerDirection.DEBIT : sign < 0 ? LedgerDirection.CREDIT : null;
  }
  return [...rows.values()];
}

/**
 * Turn the raw legs of one operation into everything the ledger pane renders: the journal entries (grouped by
 * `entry_id`, in `seq` order), the per-currency debit/credit totals at both entry and operation level, the
 * reversal linkage in both directions, and the net effect per account.
 *
 * Nothing here re-derives money: every figure is a sum of the legs the backend posted, computed with the
 * exact decimal helpers. `isBalanced` mirrors the backend's own `assertBalanced` invariant (debits = credits
 * **per currency**) — when it is false, something is genuinely wrong with the entry, not with the rounding.
 */
export function buildLedgerOperationView(legs: LedgerLeg[]): LedgerOperationView {
  const ordered = [...legs].sort((a, b) => compareLedgerSeq(a.seq, b.seq));

  const legsByEntry = new Map<string, LedgerLeg[]>();
  for (const leg of ordered) {
    const bucket = legsByEntry.get(leg.entry_id);
    if (bucket) bucket.push(leg);
    else legsByEntry.set(leg.entry_id, [leg]);
  }

  const entries: LedgerEntryGroup[] = [...legsByEntry.entries()].map(([entryId, entryLegs], position) => {
    const totals = new CurrencyTotals();
    for (const leg of entryLegs) totals.add(leg);
    const built = totals.build();
    const head = entryLegs[0];
    return {
      entryId,
      index: position + 1,
      operationType: head.operation_type,
      postedAt: head.posted_at,
      description: head.description ?? null,
      reversesEntryId: head.reverses_entry_id ?? null,
      reversesEntryIndex: null,
      reversedByIndexes: [],
      legs: entryLegs,
      totals: built,
      isBalanced: built.every((total) => total.isBalanced),
    };
  });

  // Reversal linkage in the other direction, so a reversed entry can say so on its own card.
  const indexByEntryId = new Map(entries.map((entry) => [entry.entryId, entry.index]));
  const entryByIndex = new Map(entries.map((entry) => [entry.index, entry]));
  for (const entry of entries) {
    if (!entry.reversesEntryId) continue;
    const targetIndex = indexByEntryId.get(entry.reversesEntryId);
    if (targetIndex == null) continue;
    entry.reversesEntryIndex = targetIndex;
    entryByIndex.get(targetIndex)?.reversedByIndexes.push(entry.index);
  }

  const operationTotals = new CurrencyTotals();
  for (const leg of ordered) operationTotals.add(leg);
  const totals = operationTotals.build();

  const organizationIds: string[] = [];
  const currencies: string[] = [];
  for (const leg of ordered) {
    const orgId = leg.account.scope_organization_id;
    if (orgId && !organizationIds.includes(orgId)) organizationIds.push(orgId);
    if (!currencies.includes(leg.currency)) currencies.push(leg.currency);
  }

  return {
    legs: ordered,
    entries,
    totals,
    rollup: buildRollup(ordered),
    reversalCount: entries.filter((entry) => entry.reversesEntryId !== null).length,
    isBalanced: entries.length > 0 && entries.every((entry) => entry.isBalanced),
    organizationIds,
    currencies,
    firstPostedAt: ordered.length > 0 ? ordered[0].posted_at : null,
    lastPostedAt: ordered.length > 0 ? ordered[ordered.length - 1].posted_at : null,
  };
}
